import { useEffect, useMemo, useRef, useState } from 'react'
import Layout from 'containers/Layout'
import Table from 'components/pageComponents/Table'
import Button from 'components/atoms/Button'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import PageFooter from 'components/paginationComponent'
import Selection from 'components/molecules/Selection'
import Popover from 'components/molecules/Popover'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { CalendarIcon } from '@heroicons/react/outline'
import { DateRangePicker } from 'react-date-range'
import { enUS, arSA } from 'date-fns/locale'
import { defaultStaticRanges, defaultInputRanges } from 'react-date-range/dist/defaultRanges'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { preBookingsApiGetList } from 'lib/network/apiGet'
import PreBookingStatusBadge from './PreBookingStatusBadge'
import { PreBookingNotesDrawer } from './PreBookingNotes'
import { buildListParams, formatWallClockDate, hasMorePages, ownerLabel, summariseReminders, totalPagesFrom } from './preBookingHelpers'

const PAGE_SIZE = 25

const STATUSES = ['PRE_BOOKING', 'READY_FOR_CONFIRMATION', 'CONFIRMED', 'ACTIVE', 'COMPLETED', 'ABANDONED']

const PreBookings = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const datePopoverRef = useRef(null)

  const [list, setList] = useState([])
  const [pagination, setPagination] = useState({})
  const [loading, setLoading] = useState(false)
  const [notesIntent, setNotesIntent] = useState(null)
  const [filter, setFilter] = useState({ status: '', guest: '', reminded: '', from: '', to: '' })
  const [dateSelection, setDateSelection] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
  })

  const datePickerLocale = useMemo(() => (isRTL ? arSA : enUS), [isRTL])

  const localizedStaticRanges = useMemo(() => {
    const labels = {
      Today: t('reports.datePicker.today'),
      Yesterday: t('reports.datePicker.yesterday'),
      'This Week': t('reports.datePicker.thisWeek'),
      'Last Week': t('reports.datePicker.lastWeek'),
      'This Month': t('reports.datePicker.thisMonth'),
      'Last Month': t('reports.datePicker.lastMonth'),
    }
    return defaultStaticRanges.map(range => ({
      ...range,
      label: labels[range.label] || range.label,
    }))
  }, [t])

  const localizedInputRanges = useMemo(() => {
    const labels = {
      'days up to today': t('reports.datePicker.daysUpToToday'),
      'days starting today': t('reports.datePicker.daysStartingToday'),
    }
    return defaultInputRanges.map(range => ({
      ...range,
      label: labels[range.label] || range.label,
    }))
  }, [t])

  const fetchPreBookings = async (page = 1) => {
    const isInitialPage = page === 1
    if (isInitialPage) setLoading(true)

    try {
      const res = await preBookingsApiGetList(buildListParams(filter, page, PAGE_SIZE))
      const fetchedList = res?.data?.rows || []
      // Later pages append; page 1 replaces, so a filter change never stacks
      // new results on top of stale ones.
      setList(prev => (isInitialPage ? fetchedList : [...prev, ...fetchedList]))
      setPagination({
        total: res?.data?.total || 0,
        pageNumber: res?.data?.pageNumber || page,
        pageSize: res?.data?.pageSize || PAGE_SIZE,
      })
    } finally {
      if (isInitialPage) setLoading(false)
    }
  }

  useEffect(() => {
    fetchPreBookings()
  }, [filter.status, filter.guest, filter.reminded, filter.from, filter.to])

  const applyDateRange = range => {
    const start = range?.startDate ? moment(range.startDate).startOf('day') : null
    const end = range?.endDate ? moment(range.endDate).endOf('day') : null
    setFilter(prev => ({
      ...prev,
      from: start ? start.format('YYYY-MM-DD') : '',
      to: end ? end.format('YYYY-MM-DD') : '',
    }))
  }

  const handleDateRangeChange = ranges => {
    const next = ranges?.selection || ranges?.range1
    if (!next) return
    setDateSelection({ ...next, key: 'selection' })
    applyDateRange(next)
  }

  const clearDateRange = () => {
    setDateSelection({
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    })
    setFilter(prev => ({ ...prev, from: '', to: '' }))
  }

  const statusOptions = useMemo(() => STATUSES.map(status => ({ id: status, name: t(`preBookings.statuses.${status}`, status) })), [t])

  const guestOptions = useMemo(
    () => [
      { id: 'guest', name: t('preBookings.filters.guestsOnly') },
      { id: 'user', name: t('preBookings.filters.signedInOnly') },
    ],
    [t]
  )

  const remindedOptions = useMemo(
    () => [
      { id: 'yes', name: t('preBookings.filters.remindedYes') },
      { id: 'no', name: t('preBookings.filters.remindedNo') },
    ],
    [t]
  )

  const columns = useMemo(
    () => [
      {
        name: t('preBookings.columns.car'),
        key: 'car',
        rendered: item => {
          const car = item?.carId
          const label = [car?.car?.make?.make, car?.car?.model?.modal, car?.modelYear].filter(Boolean).join(' ')
          return <span className="text-[13px]">{label || '-'}</span>
        },
      },
      {
        name: t('preBookings.columns.customer'),
        key: 'customer',
        rendered: item => (
          <div className="flex flex-col">
            <span className="text-[13px]">{ownerLabel(item, t('preBookings.guest'))}</span>
            {item?.userId?.email ? <span className="text-xs text-gray-500">{item.userId.email}</span> : null}
          </div>
        ),
      },
      { name: t('preBookings.columns.status'), key: 'status', rendered: item => <PreBookingStatusBadge status={item.status} t={t} /> },
      {
        name: t('preBookings.columns.dates'),
        key: 'dates',
        rendered: item =>
          item?.pickupDate && item?.returnDate ? (
            <span className="text-xs text-gray-600">
              {formatWallClockDate(item.pickupDate)} – {formatWallClockDate(item.returnDate)}
            </span>
          ) : (
            <span className="text-xs text-gray-400">{t('preBookings.noDates')}</span>
          ),
      },
      {
        name: t('preBookings.columns.pushCapable'),
        key: 'pushCapable',
        rendered: item => (
          <span className={`text-xs font-medium ${item.pushCapable ? 'text-green-700' : 'text-gray-400'}`}>{item.pushCapable ? t('common.yes') : t('common.no')}</span>
        ),
      },
      {
        name: t('preBookings.columns.reminders'),
        key: 'reminders',
        rendered: item => {
          const reminders = summariseReminders(item.remindersSent)
          if (reminders.length === 0) return <span className="text-xs text-gray-400">-</span>
          return (
            <div className="flex flex-wrap gap-1">
              {reminders.map(reminder => (
                <span key={reminder.stage} className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700">
                  {reminder.label}
                </span>
              ))}
            </div>
          )
        },
      },
      {
        name: t('preBookings.columns.lastActivity'),
        key: 'lastActivityAt',
        rendered: item => <span className="text-xs text-gray-600">{item.lastActivityAt ? moment(item.lastActivityAt).fromNow() : '-'}</span>,
      },
      {
        name: t('preBookings.columns.created'),
        key: 'createdAt',
        rendered: item => <span className="text-xs text-gray-600">{item.createdAt ? moment(item.createdAt).fromNow() : '-'}</span>,
      },
      {
        name: t('preBookings.columns.actions'),
        key: 'actions',
        rendered: item => (
          <div className="flex items-center gap-2">
            <Link to={`/pre-bookings/${item._id}`}>
              <Button size="sm" variant="outline" className="h-8 px-3">
                {t('preBookings.actions.view')}
              </Button>
            </Link>
            <Button size="sm" variant="outline" className="h-8 px-3" onClick={() => setNotesIntent(item)}>
              {t('preBookings.actions.notes')}
            </Button>
          </div>
        ),
      },
    ],
    [t]
  )

  return (
    <Layout
      title={t('preBookings.title')}
      headerRight={
        <div className="flex flex-col sm:flex-row items-start sm:items-center w-full justify-end sm:mt-0 sm:ml-4 gap-2 sm:gap-3">
          <PageFooter currentPage={pagination.pageNumber} totalPages={totalPagesFrom(pagination.total, pagination.pageSize)} totalRecords={pagination.total} />
          <Popover
            custom
            placement="right"
            ref={datePopoverRef}
            content={close => (
              <DateRangePicker
                weekStartsOn={0}
                locale={datePickerLocale}
                staticRanges={localizedStaticRanges}
                inputRanges={localizedInputRanges}
                startDatePlaceholder={t('reports.datePicker.early')}
                endDatePlaceholder={t('reports.datePicker.continuous')}
                footerContent={
                  <>
                    <button
                      type="button"
                      onClick={close}
                      className="w-[100px] mt-4 py-2 mr-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
                    >
                      {t('reports.datePicker.close')}
                    </button>
                    <button
                      type="button"
                      disabled={!filter.from && !filter.to}
                      onClick={() => {
                        clearDateRange()
                        close()
                      }}
                      className="w-[100px] mt-4 py-2 mr-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none disabled:opacity-50"
                    >
                      {t('reports.datePicker.clear')}
                    </button>
                  </>
                }
                ranges={[dateSelection]}
                onChange={handleDateRangeChange}
              />
            )}
          >
            <button
              type="button"
              className="inline-flex w-full sm:w-[250px] h-[37px] justify-center items-center border border-gray-300 shadow-sm text-sm leading-4 font-regular rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none px-3"
            >
              <CalendarIcon className={`h-4 w-4 text-gray-500 shrink-0 ${isRTL ? 'ml-3' : 'mr-3'}`} />
              <span className="truncate">
                {filter.from && filter.to ? `${moment(filter.from).format('MMM Do, YYYY')} - ${moment(filter.to).format('MMM Do, YYYY')}` : t('reports.filters.selectDateRange')}
              </span>
            </button>
          </Popover>
          <Selection
            className="w-[170px]"
            usePortalOptions
            value={filter.status}
            onChange={option => setFilter(prev => ({ ...prev, status: prev.status === option?.id ? '' : option?.id || '' }))}
            list={statusOptions}
            title={t('preBookings.filters.status')}
          />
          <Selection
            className="w-[150px]"
            usePortalOptions
            value={filter.guest}
            onChange={option => setFilter(prev => ({ ...prev, guest: prev.guest === option?.id ? '' : option?.id || '' }))}
            list={guestOptions}
            title={t('preBookings.filters.customerType')}
          />
          <Selection
            className="w-[150px]"
            usePortalOptions
            value={filter.reminded}
            onChange={option => setFilter(prev => ({ ...prev, reminded: prev.reminded === option?.id ? '' : option?.id || '' }))}
            list={remindedOptions}
            title={t('preBookings.filters.reminded')}
          />
        </div>
      }
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {loading ? (
                  <div className="h-[220px] flex items-center justify-center">
                    <LoadingIcon />
                  </div>
                ) : list?.length === 0 ? (
                  <div className="py-10">
                    <EmptyState v2 description={t('common.noData')} />
                  </div>
                ) : (
                  <Table columns={columns} data={list} fetchData={() => fetchPreBookings((pagination?.pageNumber || 1) + 1)} hasMore={hasMorePages(pagination)} />
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <PreBookingNotesDrawer intent={notesIntent} onClose={() => setNotesIntent(null)} t={t} />
    </Layout>
  )
}

export default PreBookings
